<script setup lang="ts">
import { PropType } from 'vue';
import { useDisplay } from 'vuetify';
import useModal from '../composables/useModal';
import { VBtn } from 'vuetify/components/VBtn';
import { useLocale } from '../composables/useLocale';

const { xs } = useDisplay();
const { t } = useLocale();

const props = defineProps({
  name: {
    type: String,
    default: null,
    required: true,
  },

  loading: {
    type: Boolean,
    default: false,
  },

  title: {
    type: String,
    default: null,
    required: false,
  },

  titleClass: {
    type: String,
    default: null,
    required: false,
  },

  titlePrependIcon: {
    type: String,
    default: '',
  },

  showCloseBtn: {
    type: Boolean,
    default: true,
  },
  closeBtnAction: {
    type: Function,
    default: null,
  },

  action: {
    type: Function,
    default: null,
  },
  actionLabel: {
    type: String,
    default: '',
  },
  actionColor: {
    type: String,
    default: 'primary',
  },
  actionVariant: {
    type: String as PropType<VBtn['variant']>,
    default: 'flat',
  },
  actionPrependIcon: {
    type: String,
    default: '',
  },
  actionAppendIcon: {
    type: String,
    default: '',
  },
  actionDisabled: {
    type: Boolean,
    default: false,
  },

  cancelAction: {
    type: Function,
    default: null,
  },
  cancelDisabled: {
    type: Boolean,
    default: false,
  },
  showCancelAction: {
    type: Boolean,
    default: true,
  },
  cancelActionLabel: {
    type: String,
    default: '',
  },
  cancelActionColor: {
    type: String,
    default: 'primary',
  },
  cancelActionVariant: {
    type: String as PropType<VBtn['variant']>,
    default: 'outlined',
  },

  secondaryAction: {
    type: Function,
    default: null,
  },
  secondaryActionDisabled: {
    type: Boolean,
    default: false,
  },
  secondaryActionLabel: {
    type: String,
    default: '',
  },
  secondaryActionColor: {
    type: String,
    default: 'secondary',
  },
  secondaryActionVariant: {
    type: String as PropType<VBtn['variant']>,
    default: 'outlined',
  },
  secondaryActionPrependIcon: {
    type: String,
    default: '',
  },

  centerActions: {
    type: Boolean,
    default: false,
  },

  topLeftBtnAction: {
    type: Function,
    default: null,
  },
  topLeftBtnIcon: {
    type: String,
    default: 'mdi-close',
  },
  topLeftBtnLabel: {
    type: String,
    default: '',
  },

  width: {
    type: String,
    default: '560',
  },

  fullscreen: {
    type: Boolean,
    default: false,
  },
  persistent: {
    type: Boolean,
    default: false,
  },
  contained: {
    type: Boolean,
    default: false,
  },
  growContents: {
    type: Boolean,
    default: true,
  },

  backgroundColor: {
    type: String,
    default: '',
  },

  /**
   * If true, the dialog will not close automatically
   * when:
   *  - on default cancel action
   *  - either top close buttons are clicked
   *  - escape key
   *  - clicking outside.
   *
   * Dialog must be closed programmatically by calling `close()` method.
   */
  disableAutoClose: {
    type: Boolean,
    default: false,
  },
});

const emits = defineEmits<{
  (e: 'close'): void;
}>();

const { isOpen, close } = useModal(props.name as any);
const onClose = () => {
  emits('close');
  if (!props.disableAutoClose) {
    close();
  }
};
</script>

<template>
  <v-dialog
    v-model="isOpen"
    :persistent="persistent"
    :contained="contained"
    :fullscreen="fullscreen"
    :width="fullscreen || xs ? undefined : width"
    @click:outside="onClose"
    @keydown.esc="onClose"
    min-width="340"
  >
    <v-card :loading="loading" :color="backgroundColor">
      <v-card-item class="d-flex align-center justify-space-between">
        <template v-slot:prepend>
          <span class="d-flex justify-start" :style="{ 'min-width': topLeftBtnAction ? '48px' : '0' }">
            <v-btn
              v-if="topLeftBtnAction"
              variant="text"
              :disabled="loading"
              :prepend-icon="topLeftBtnIcon"
              :icon="!topLeftBtnLabel || xs"
              @click="topLeftBtnAction"
            >
              {{ xs ? '' : topLeftBtnLabel }}
              <v-icon v-if="!topLeftBtnLabel || xs">{{ topLeftBtnIcon }}</v-icon>
            </v-btn>
          </span>
        </template>
        <v-card-title
          class="d-flex align-center ga-2"
          :class="{
            'mr-12 pr-16': topLeftBtnAction && !secondaryAction && !xs,
            'mr-12': topLeftBtnAction && !secondaryAction && xs,
            'ml-12': !topLeftBtnAction && showCloseBtn,
          }"
        >
          <v-icon v-if="titlePrependIcon" class="mb-1" size="small">{{ titlePrependIcon }}</v-icon>
          <span class="flex-d justify-center" :class="titleClass" style="font-size: clamp(1rem, 5vw, 1.25rem)">
            {{ title }}
          </span>
        </v-card-title>
        <template v-slot:append>
          <span class="d-flex flex-0-0 justify-end" :style="{ 'min-width': showCloseBtn ? '48px' : '0' }">
            <v-square-btn
              v-if="showCloseBtn"
              variant="text"
              :disabled="loading"
              icon
              :aria-label="t('lcp.close')"
              @click="closeBtnAction ? closeBtnAction() : onClose()"
            >
              <v-icon>mdi-close</v-icon>
            </v-square-btn>
          </span>
        </template>
      </v-card-item>
      <v-card-text :class="{ 'flex-grow-0': growContents }">
        <slot></slot>
      </v-card-text>
      <v-card-actions>
        <div class="d-flex flex-wrap ga-4 w-100" :class="{ 'd-flex': centerActions, 'justify-center': centerActions }">
          <v-btn
            v-if="secondaryAction"
            :disabled="loading || secondaryActionDisabled"
            :variant="secondaryActionVariant"
            :color="secondaryActionColor"
            :prepend-icon="secondaryActionPrependIcon"
            @click="secondaryAction"
            :block="xs"
          >
            {{ secondaryActionLabel }}
          </v-btn>
          <v-spacer v-if="!centerActions"></v-spacer>
          <v-btn
            v-if="showCancelAction"
            :variant="cancelActionVariant"
            :color="cancelActionColor"
            :disabled="loading || cancelDisabled"
            @click="cancelAction ? cancelAction() : onClose()"
            :block="xs"
            >{{ cancelActionLabel || t('lcp.cancel') }}</v-btn
          >
          <v-btn
            v-if="action"
            :variant="actionVariant"
            :color="actionColor"
            :prepend-icon="actionPrependIcon"
            :append-icon="actionAppendIcon"
            :disabled="actionDisabled || loading"
            :block="xs"
            @click="action"
          >
            {{ actionLabel || t('lcp.save') }}
          </v-btn>
        </div>
      </v-card-actions>
    </v-card>
  </v-dialog>
</template>
